import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import { isAdminUser } from "@chefaiq/shared/lib/admin";
import { apiRequest } from "@/lib/queryClient";
import { copyToClipboard } from "@/lib/clipboard";
import { generateCampaignURL } from "@/lib/campaign-url";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Plus, Trash2, TrendingUp, Users, DollarSign, Target, BarChart3, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const SOURCES = [
  { value: 'email', label: 'Email' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'paid_ads', label: 'Paid Ads' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'direct', label: 'Direct' },
  { value: 'other', label: 'Other' },
];

export default function CampaignManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    totalCost: "",
    startDate: "",
    endDate: "",
    description: "",
  });

  const { data: campaignsData, isLoading: loading } = useQuery({
    queryKey: ["/api/admin/campaigns"],
    queryFn: () => apiRequest("GET", "/api/admin/campaigns") as unknown as Promise<any>,
  });

  const { data: analytics } = useQuery({
    queryKey: ["/api/admin/campaigns/analytics/summary"],
    queryFn: () => apiRequest("GET", "/api/admin/campaigns/analytics/summary") as unknown as Promise<any>,
  });

  const currentDomain = window.location.origin;
  const campaigns = ((campaignsData as any)?.campaigns ?? []).map((c: any) => ({
    ...c,
    url: generateCampaignURL(c, currentDomain),
  }));

  const fetchData = () => {
    qc.invalidateQueries({ queryKey: ["/api/admin/campaigns"] });
    qc.invalidateQueries({ queryKey: ["/api/admin/campaigns/analytics/summary"] });
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.code.trim()) {
      toast({ title: "Error", description: "Campaign name and code are required", variant: "destructive" });
      return;
    }

    try {
      const payload = {
        name: formData.name,
        customCode: formData.code,
        totalCost: parseFloat(formData.totalCost) || 0,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        description: formData.description,
      };

      const data = await apiRequest("POST", "/api/admin/campaigns", payload) as any;

      const currentDomain = window.location.origin;
      const campaignWithUrl = {
        ...data.campaign,
        url: generateCampaignURL(data.campaign, currentDomain)
      };
      
      setFormData({ name: "", code: "", totalCost: "", startDate: "", endDate: "", description: "" });
      setShowForm(false);
      toast({ title: "Success", description: "Campaign created" });
      fetchData();
    } catch (error: any) {
      toast({ title: "Error", description: error?.message || "Failed to create campaign", variant: "destructive" });
    }
  };

  const handleDelete = async (campaign: any) => {
    // Check if campaign has data
    const hasData = campaign.clicks > 0 || campaign.signups > 0 || campaign.paidUsers > 0;
    
    if (hasData) {
      const confirmMessage = `This campaign has data:\n- ${campaign.clicks} clicks\n- ${campaign.signups} signups\n- ${campaign.paidUsers || 0} paid users\n\nDeleting will remove all tracking data. Are you sure?`;
      if (!confirm(confirmMessage)) return;
    } else {
      if (!confirm("Delete this campaign?")) return;
    }
    
    try {
      await apiRequest("DELETE", `/api/admin/campaigns/${campaign.code}`);
      toast({ title: "Success", description: "Campaign deleted" });
      fetchData();
    } catch (error: any) {
      toast({ title: "Error", description: error?.message || "Failed to delete campaign", variant: "destructive" });
    }
  };

  const handleEdit = (campaign: any) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      code: campaign.code,
      totalCost: campaign.totalCost?.toString() || "",
      startDate: campaign.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : "",
      endDate: campaign.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : "",
      description: campaign.description || "",
    });
    setShowForm(true);
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast({ title: "Error", description: "Campaign name is required", variant: "destructive" });
      return;
    }

    try {
      const payload = {
        name: formData.name,
        totalCost: parseFloat(formData.totalCost) || 0,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        description: formData.description,
      };

      await apiRequest("PATCH", `/api/admin/campaigns/${(editingCampaign as any)?.code}`, payload);
      
      toast({ title: "Success", description: "Campaign updated" });
      setFormData({ name: "", code: "", totalCost: "", startDate: "", endDate: "", description: "" });
      setEditingCampaign(null);
      setShowForm(false);
      fetchData();
    } catch (error: any) {
      toast({ title: "Error", description: error?.message || "Failed to update campaign", variant: "destructive" });
    }
  };

  const handleCopy = async (text: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      toast({ title: "Copied!", description: "URL copied to clipboard" });
    } else {
      toast({ title: "Error", description: "Failed to copy", variant: "destructive" });
    }
  };

  if (!isAdminUser(user)) return <div className="p-6">Access denied</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold">UTM Tracking & Attribution</h1>
          <p className="text-muted-foreground">Track traffic sources and campaign performance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>Refresh</Button>
          <Button onClick={() => setShowForm(!showForm)}><Plus className="h-4 w-4 mr-2" />New Campaign</Button>
        </div>
      </div>

      {analytics && (
        <div className="grid gap-4 md:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.totalClicks?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground">{analytics.totalUniqueVisitors?.toLocaleString() || 0} unique</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Signups</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.totalSignups?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground">{analytics.totalPaidUsers || 0} paid</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.conversionRate || '0.00'}%</div>
              <p className="text-xs text-muted-foreground">Clicks to signups</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₱{analytics.totalRevenue?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground">CAC: ₱{analytics.avgCac || '0.00'}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">ROI</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${parseFloat(analytics.roi) > 0 ? 'text-green-600' : parseFloat(analytics.roi) < 0 ? 'text-red-600' : ''}`}>
                {analytics.roi || '0.00'}%
              </div>
              <p className="text-xs text-muted-foreground">LTV: ₱{analytics.avgLtv || '0.00'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="campaigns">
        <TabsList>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="sources">By Channels</TabsTrigger>
        </TabsList>
        
        <TabsContent value="campaigns" className="space-y-4">
          {showForm && (
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">{editingCampaign ? 'Edit Campaign' : 'Create Campaign'}</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Campaign Name *</label>
                  <Input placeholder="Facebook Ad - January 2026" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </div>
                
                <div>
                  <label className="text-sm font-medium">Campaign Code *</label>
                  <Input 
                    placeholder="fb-jan-2026" 
                    value={formData.code} 
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })} 
                    disabled={!!editingCampaign}
                  />
                  {editingCampaign && <p className="text-xs text-muted-foreground mt-1">Code cannot be changed</p>}
                </div>
                
                <div>
                  <label className="text-sm font-medium">Campaign Cost (₱)</label>
                  <Input type="number" placeholder="0.00" value={formData.totalCost} onChange={(e) => setFormData({ ...formData, totalCost: e.target.value })} />
                </div>
                
                <div>
                  <label className="text-sm font-medium">Start Date</label>
                  <Input type="date" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} />
                </div>
                
                <div>
                  <label className="text-sm font-medium">End Date</label>
                  <Input type="date" value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea placeholder="Optional notes" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
              </div>

              <div className="flex gap-2">
                <Button onClick={editingCampaign ? handleUpdate : handleCreate}>
                  {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                </Button>
                <Button variant="outline" onClick={() => {
                  setShowForm(false);
                  setEditingCampaign(null);
                  setFormData({ name: "", code: "", totalCost: "", startDate: "", endDate: "", description: "" });
                }}>Cancel</Button>
              </div>
            </Card>
          )}

          {loading ? (
            <div>Loading...</div>
          ) : campaigns.length === 0 ? (
            <Card className="p-12 text-center"><p className="text-muted-foreground">No campaigns yet</p></Card>
          ) : (
            <div className="grid gap-4">
              {campaigns.map((c: any) => (
                <Card key={c._id} className="p-6">
                  <div className="flex flex-wrap justify-between items-start gap-2">
                    <div className="flex-1 cursor-pointer" onClick={() => setExpandedCampaign(expandedCampaign === c._id ? null : c._id)}>
                      <div className="flex items-center gap-2">
                        <h3 className="text-xl font-semibold">{c.name}</h3>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">{SOURCES.find(s => s.value === c.source)?.label}</span>
                        {expandedCampaign === c._id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </div>
                      {c.description && <p className="text-sm text-muted-foreground mt-1">{c.description}</p>}
                      
                      <div className="mt-4 flex items-center gap-2">
                        <code className="bg-muted px-3 py-1 rounded text-sm">{c.code}</code>
                        <Button size="sm" variant="ghost" onClick={() => handleCopy(c.url)}><Copy className="h-4 w-4" /></Button>
                      </div>

                      <div className="text-xs text-muted-foreground mt-2 break-all">{c.url}</div>

                      <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-4">
                        <div>
                          <div className="text-2xl font-bold">{c.clicks}</div>
                          <div className="text-sm text-muted-foreground">Clicks</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">{c.uniqueVisitors}</div>
                          <div className="text-sm text-muted-foreground">Unique</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">{c.signups}</div>
                          <div className="text-sm text-muted-foreground">Signups</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">{c.paidUsers || 0}</div>
                          <div className="text-sm text-muted-foreground">Paid Users</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">{c.conversionRate}%</div>
                          <div className="text-sm text-muted-foreground">Conv Rate</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">₱{c.totalRevenue}</div>
                          <div className="text-sm text-muted-foreground">Revenue</div>
                        </div>
                      </div>
                      
                      <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                        <div><span className="text-muted-foreground">CAC:</span> ₱{c.cac}</div>
                        <div><span className="text-muted-foreground">LTV:</span> ₱{c.ltv}</div>
                        <div className={parseFloat(c.roi) > 0 ? "text-green-600 font-semibold" : parseFloat(c.roi) < 0 ? "text-red-600 font-semibold" : ""}>
                          <span className="text-muted-foreground">ROI:</span> {c.roi}%
                        </div>
                      </div>
                      
                      {expandedCampaign === c._id && (
                        <div className="mt-6 pt-6 border-t">
                          <h4 className="font-semibold mb-4">Content Breakdown</h4>
                          <div className="space-y-3">
                            {c.content ? (
                              <div className="p-4 bg-muted rounded-lg">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <div className="font-medium">{c.content}</div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                      URL: {generateCampaignURL({ ...c, content: c.content }, window.location.origin)}
                                    </div>
                                  </div>
                                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleCopy(generateCampaignURL({ ...c, content: c.content }, window.location.origin)); }}>
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-8 text-muted-foreground">
                                <p>No content variations yet</p>
                                <p className="text-sm mt-2">Add utm_content parameter to track different content variations</p>
                                <p className="text-xs mt-2">Example: Founder Video, Launch Email, Ad Variant A</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(c)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(c)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="sources">
          {(analytics as any)?.bySource && (
            <div className="grid gap-4">
              {Object.entries((analytics as any).bySource).map(([source, data]: [string, any]) => (
                <Card key={source} className="p-6">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold">{SOURCES.find(s => s.value === source)?.label || source}</h3>
                      <div className="mt-2 flex flex-wrap gap-3 text-sm">
                        <div><span className="text-muted-foreground">Clicks:</span> <span className="font-semibold">{data.clicks}</span></div>
                        <div><span className="text-muted-foreground">Signups:</span> <span className="font-semibold">{data.signups}</span></div>
                        <div><span className="text-muted-foreground">Revenue:</span> <span className="font-semibold">₱{data.revenue}</span></div>
                        <div><span className="text-muted-foreground">Conv Rate:</span> <span className="font-semibold">{data.clicks > 0 ? ((data.signups / data.clicks) * 100).toFixed(2) : 0}%</span></div>
                      </div>
                    </div>
                    <BarChart3 className="h-8 w-8 text-muted-foreground" />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
